import { Controller } from '@hotwired/stimulus'
import tippy, { Props, Instance as TippyInstance } from 'tippy.js'

export interface HTMLElementWithTooltip extends HTMLElement {
  tooltip: TippyInstance
}

export default class TooltipController extends Controller<HTMLElementWithTooltip> {
  static targets = ['trigger']

  declare observer: MutationObserver
  declare readonly triggerTargets: HTMLElement[]

  connect() {
    this.element.tooltip = tippy(this.element, this.getProps())

    if (this.element.dataset.tooltipObserve === 'true') this.observe()
    if (this.element.dataset.tooltipOverflow === 'true') this.addOverflowListeners()
  }

  disconnect() {
    if (this.element.dataset.tooltipOverflow === 'true') this.removeOverflowListeners()
    this.element.tooltip.destroy()
  }

  // "private" methods

  addOverflowListeners() {
    window.addEventListener('resize', this.rerender.bind(this))
    this.element.addEventListener('mouseenter', this.showIfContentOverflows.bind(this))
  }

  getContent() {
    const { dataset, title } = this.element
    const { tooltipOverflow, tooltipTemplate, tooltipText } = dataset
    const ariaLabel = this.element.getAttribute('aria-label')

    const content =
      tooltipOverflow === 'true' && this.element.innerText
        ? this.element.innerText
        : tooltipTemplate
          ? document.getElementById(tooltipTemplate)!.innerHTML
          : tooltipText
            ? this.sanitized(tooltipText)
            : ariaLabel
              ? ariaLabel
              : title

    if (title && content === title) {
      // Preserve the tooltipText in case reRender is called.
      this.element.dataset.tooltipText = title
      this.element.removeAttribute('title')
    }

    if (!content) {
      console.warn('TooltipController: No tooltip content provided, defaulting to innerText')
      return this.element.innerText
    }

    return content
  }

  getAppendTo(): Props['appendTo'] {
    return (this.element.closest('dialog.new-drawer') as HTMLElement) || document.body
  }

  getProps() {
    const content = this.getContent()
    const tippyProps = JSON.parse(this.element.dataset.tooltipOptions || '{}') as Partial<Props>
    const { tooltipOverflow } = this.element.dataset

    const props: Partial<Props> = {
      allowHTML: true,
      appendTo: this.getAppendTo(),
      content,
      maxWidth: 1500,
      trigger: tooltipOverflow ? 'manual' : 'mouseenter focus',
      triggerTarget: this.triggerTargets.length ? this.triggerTargets : null,
      ...tippyProps,
    }

    return props
  }

  observe() {
    this.observer = new MutationObserver(
      mutations => mutations.filter(m => m.attributeName?.startsWith('data-tooltip')).length && this.rerender(),
    )

    this.observer.observe(this.element, { attributes: true })
  }

  removeOverflowListeners() {
    window.removeEventListener('resize', this.rerender.bind(this))
    this.element.removeEventListener('mouseenter', this.showIfContentOverflows.bind(this))
  }

  rerender() {
    const tippyProps = this.getProps()

    // NOTE: Tippy may no longer exist at this point
    this.element.tooltip?.setProps(tippyProps)
  }

  /**
   * Takes a string of HTML and returns a sanitized version of it
   */
  sanitized(content: string) {
    const parser = new DOMParser()
    return parser.parseFromString(content, 'text/html').body.innerHTML
  }

  showIfContentOverflows(event: MouseEvent) {
    const target = event.target as HTMLElement
    const isOverflowing = target.scrollWidth > target.clientWidth

    if (!isOverflowing) return

    this.element.tooltip.show()

    target.addEventListener(
      'mouseleave',
      () => {
        this.element.tooltip.hide()
      },
      { once: true },
    )
  }
}
